import { cva } from "class-variance-authority";
import * as React from "react";

//icons
import { faXmarkCircle } from "@performyard/fontawesome/pro-solid-svg-icons";

//utils
import { cn } from "../../../lib/utils";

//context
import { useInputField } from "../../molecules/InputWrapper/InputContext";
//atoms
import { ButtonIcon } from "../ButtonIcon";
import { IconProps } from "../Icon";
import { Icon } from "../Icon";

type InputSize = "small" | "medium";

export const inputContainerVariants = cva(
	"group relative box-border flex items-center justify-start gap-2 rounded-1 border border-solid border-neutral-3 bg-neutral-0",
	{
		variants: {
			size: {
				medium: "min-h-8 px-2",
				small: "min-h-6 px-1",
			},
			errored: {
				true: "!border-errored focus-within:!border-errored hover:!border-errored active:!border-errored",
			},
			disabled: {
				true: "cursor-not-allowed opacity-disabled",
				false: "cursor-default hover:border-neutral-5 active:border-neutral-5",
			},
			readOnly: {
				true: "bg-neutral-1 focus-within:border-neutral-10 hover:border-neutral-3 focus-within:hover:border-neutral-10 active:border-neutral-3",
			},
			flash: {
				true: "animate-input-flash",
			},
		},
		compoundVariants: [
			// read-only text stays selectable/copyable, so signal it with a text cursor
			// (matches TextEditor's read-only handling); disabled keeps cursor-not-allowed
			{
				readOnly: true,
				disabled: false,
				class: "cursor-text",
			},
		],
		defaultVariants: {
			size: "medium",
			errored: false,
			disabled: false,
			readOnly: false,
			flash: false,
		},
	}
);

const inputVariants = cva(
	"w-full border-none bg-transparent font-regular outline-hidden placeholder:text-neutral-5 focus-visible:border-x-0 focus-visible:border-t-0 focus-visible:border-b-2 focus-visible:border-primary-5 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:!border-none disabled:!bg-transparent",
	{
		variants: {
			size: {
				medium: "text-paragraph-3",
				small: "text-caption",
			},
		},
		defaultVariants: {
			size: "medium",
		},
	}
);

/**
 * Drives a one-shot blue→white flash from a changing `flashKey`. Returns `flashing`
 * (apply the `flash` cva variant when true) and an `onAnimationEnd` handler that clears it.
 * Restart is React-safe: it toggles state off → next frame → on so the class is removed
 * and re-added across paints, replaying the CSS animation. The initial `flashKey` never
 * flashes — only subsequent changes do.
 */
export const useInputFlash = (flashKey?: number) => {
	const [flashing, setFlashing] = React.useState(false);
	const prevFlashKey = React.useRef(flashKey);

	React.useEffect(() => {
		if (flashKey === undefined || flashKey === prevFlashKey.current) return;
		prevFlashKey.current = flashKey;
		setFlashing(false);
		const raf = requestAnimationFrame(() => setFlashing(true));
		return () => cancelAnimationFrame(raf);
	}, [flashKey]);

	const onAnimationEnd = (e: React.AnimationEvent<HTMLElement>) => {
		if (e.animationName === "input-flash") setFlashing(false);
	};

	return { flashing, onAnimationEnd };
};

/**
 * Props for the InputBase component
 *
 * @example
 * ```tsx
 * // Basic usage with value control
 * <InputBase
 *   value={inputValue}
 *   onChange={setInputValue}
 *   placeholder="Enter text..."
 * />
 *
 * // With left icon and clearable
 * <InputBase
 *   leftIcon={faSearch}
 *   clearable
 *   value={searchValue}
 *   onChange={setSearchValue}
 * />
 *
 * // Numeric input with validation
 * <InputBase
 *   type="number"
 *   inputMode="numeric"
 *   min={0}
 *   max={100}
 *   value={numericValue}
 *   onChange={setNumericValue}
 * />
 * ```
 */
type InputBaseProps = {
	// Size
	/**
	 * Size variant for the input.
	 * - "medium" (default): 32px height, paragraph-3 text
	 * - "small": 24px height, caption text
	 * @default "medium"
	 */
	size?: InputSize;

	// Visual & Layout Props
	/**
	 * Icon to display on the left side of the input field. Uses FontAwesome icons.
	 * Commonly used for search (faSearch), user (faUser), or other contextual icons.
	 *
	 * @example faSearch, faUser, faEnvelope
	 * @default undefined
	 */
	leftIcon?: IconProps["icon"];

	/**
	 * Custom CSS classes for the input container. Use with caution as this can override
	 * component styling. Prefer using design system utilities when possible.
	 *
	 * @example "bg-custom-background border-custom-color"
	 */
	dangerouslySetClassName?: string;

	/**
	 * Custom CSS classes for the input element itself. Use with caution as this can
	 * override component styling. Prefer using design system utilities when possible.
	 *
	 * @example "text-lg font-bold placeholder:text-custom-color"
	 */
	dangerouslySetInputClassName?: string;

	/**
	 * Element to display on the right side of the input, after the clear button (if present).
	 * Useful for action buttons, additional icons, or custom controls.
	 *
	 * @example <Button size="sm">Submit</Button>
	 */
	rightActionElement?: React.ReactNode;

	// State Props
	/**
	 * Whether the input should display in an error state with error styling.
	 * When true, applies error border color and styles. Often used in conjunction
	 * with InputWrapper for complete form validation display.
	 *
	 * @default false
	 */
	errored?: boolean;

	/**
	 * Whether the input is disabled. Disabled inputs cannot be focused, edited,
	 * or interacted with. Applies disabled styling and cursor.
	 *
	 * @default false
	 */
	disabled?: boolean;

	/**
	 * Whether the input is read-only. Read-only inputs display their value and remain
	 * focusable (their text can be selected and copied) but cannot be edited. Unlike
	 * disabled, read-only keeps full text contrast and does not dim the input.
	 *
	 * @default false
	 */
	readOnly?: boolean;

	/**
	 * Whether to show a clear button (X icon) on the right side of the input.
	 * When clicked, clears the input value and refocuses the input.
	 *
	 * @default false
	 */
	clearable?: boolean;

	/** Change this number to play a one-time blue→white background flash (e.g. after an applied AI suggestion). */
	flashKey?: number;

	// Value & Event Props
	/**
	 * Current value of the input field. This is a controlled component,
	 * so this prop is required for proper functionality.
	 *
	 * @example "user input text"
	 */
	value?: string;

	/**
	 * Callback fired when the input value changes. Receives the new string value.
	 * Essential for controlled component behavior.
	 *
	 * @param value - The new input value as a string
	 * @example (value) => setInputValue(value)
	 */
	onChange?: (value: string) => void;

	/**
	 * Callback fired when the input receives focus.
	 * Useful for analytics, validation triggers, or UI state changes.
	 *
	 * @param e - React focus event
	 */
	onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;

	/**
	 * Callback fired when the input loses focus.
	 * Commonly used for validation, formatting, or saving draft data.
	 *
	 * @param e - React focus event
	 */
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;

	/**
	 * Callback fired when a key is pressed while the input is focused.
	 * Useful for handling special keys like Enter, Escape, or custom shortcuts.
	 *
	 * @param e - React keyboard event
	 * @example (e) => { if (e.key === 'Enter') handleSubmit(); }
	 */
	onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;

	// HTML Attributes
	/**
	 * Unique identifier for the input element. If not provided, a unique ID
	 * will be generated automatically. Used for accessibility and form associations.
	 *
	 * @example "user-email-input"
	 */
	id?: string;

	/**
	 * Test identifier for automated testing. Follows the pattern of kebab-case naming.
	 * Clear button will automatically get a `-clear-button` suffix.
	 *
	 * @example "search-input" (clear button becomes "search-input-clear-button")
	 */
	"data-testid"?: string;

	/**
	 * Name attribute for the input element. Important for form submission
	 * and accessibility. Should match the field name in your data model.
	 *
	 * @example "email", "firstName", "searchQuery"
	 */
	name?: string;

	/**
	 * Placeholder text displayed when the input is empty. Should provide
	 * helpful guidance about the expected input format or content.
	 *
	 * @example "Enter your email address", "Search products..."
	 */
	placeholder?: string;

	// Input Type & Validation Props
	/**
	 * HTML input type attribute. Determines the input's behavior and validation.
	 * Controls things like virtual keyboard on mobile devices and browser validation.
	 *
	 * @example "text", "email", "password", "tel", "url", "number"
	 * @default "text"
	 */
	type?: HTMLInputElement["type"];

	/**
	 * Input mode hint for virtual keyboards on mobile devices. Optimizes
	 * the keyboard layout for the expected input type.
	 *
	 * @example "text", "numeric", "decimal", "email", "tel", "url", "search"
	 */
	inputMode?: React.InputHTMLAttributes<HTMLInputElement>["inputMode"];

	/**
	 * Regular expression pattern for input validation. Browser will validate
	 * input against this pattern and show error states accordingly.
	 *
	 * @example "[0-9]{3}-[0-9]{3}-[0-9]{4}" for phone numbers
	 */
	pattern?: HTMLInputElement["pattern"];

	/**
	 * Minimum number of characters required. Browser validation will prevent
	 * form submission if input is shorter than this value.
	 *
	 * @example 8 for password fields
	 */
	minLength?: HTMLInputElement["minLength"];

	/**
	 * Maximum number of characters allowed. Browser will prevent typing
	 * beyond this limit.
	 *
	 * @example 255 for text fields, 10 for phone numbers
	 */
	maxLength?: HTMLInputElement["maxLength"];

	/**
	 * Minimum numeric value (for number inputs). Browser validation will
	 * show error if value is below this number.
	 *
	 * @example 0 for non-negative numbers, 18 for age restrictions
	 */
	min?: number;

	/**
	 * Maximum numeric value (for number inputs). Browser validation will
	 * show error if value is above this number.
	 *
	 * @example 100 for percentage fields, 999 for quantity limits
	 */
	max?: number;

	/**
	 * Whether to enable browser spell checking for the input content.
	 * Useful to disable for inputs like usernames, codes, or technical terms.
	 *
	 * @default true (browser default)
	 * @example false for username fields, true for comment fields
	 */
	spellCheck?: boolean;

	/**
	 * Accessible label for the input when no visible label is present.
	 * Used by screen readers to announce the purpose of the input.
	 *
	 * @example "Search employees", "Enter email address"
	 */
	"aria-label"?: string;
};

/**
 * **InputBase - Core Input Field Component**
 *
 * **⚠️ INTERNAL USE ONLY** - This component is designed for internal py-components usage.
 * Use specific input implementations like `InputText`, `InputNumeric`, `InputDate`, etc. when importing from py-components.
 *
 * ## Overview
 * InputBase is the foundational controlled input component that provides consistent styling,
 * behavior, and accessibility features across the PerformYard design system. It supports
 * icons, validation states, clearing functionality, and integrates with InputWrapper for
 * complete form field implementation.
 *
 */
const InputBase = React.forwardRef<HTMLInputElement, InputBaseProps>(
	(
		{
			size = "medium",
			errored,
			leftIcon,
			clearable = false,
			flashKey,
			dangerouslySetClassName,
			dangerouslySetInputClassName,
			rightActionElement,
			onChange,
			id: propsId,
			readOnly: readOnlyProp,
			...props
		},
		ref
	) => {
		// use an input context to get info around the input, specifically from the nearest parent InputWrapper
		const { id: contextId, error, disabled = props?.disabled, readOnly = readOnlyProp } = useInputField();
		const generatedId = React.useId();
		//generate a unique id for the input unless one is provided through props or context
		const id = propsId || contextId || generatedId;
		//check if the input has an error
		const hasError = !!error || errored;
		const { flashing, onAnimationEnd } = useInputFlash(flashKey);

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			onChange?.(e.target.value);
		};

		//clear the input
		const handleClear = () => {
			onChange?.("");
			//focus on the input again after the button is pressed
			const input = document.getElementById(id);
			input?.focus();
		};

		return (
			<div
				data-testid={id ? `${id}-base-input` : "base-input"}
				{...(readOnly && { "data-readonly": true })}
				className={cn(
					inputContainerVariants({ size, errored: hasError, disabled, readOnly, flash: flashing }),
					dangerouslySetClassName
				)}
				onAnimationEnd={onAnimationEnd}
			>
				{leftIcon && <Icon icon={leftIcon} size={size === "small" ? 12 : 16} color="neutral-5" />}
				<input
					ref={ref}
					className={cn(inputVariants({ size }), dangerouslySetInputClassName)}
					id={id}
					disabled={disabled}
					readOnly={readOnly}
					aria-disabled={disabled}
					aria-readonly={readOnly}
					aria-invalid={hasError}
					onChange={handleChange}
					{...props}
				/>
				{clearable && props.value && !readOnly && (
					<ButtonIcon
						icon={faXmarkCircle}
						size={size === "small" ? 12 : 16}
						color="neutral"
						shade={3}
						onClick={handleClear}
						disabled={disabled}
						aria-label="Clear"
						aria-disabled={disabled}
						data-testid={`${id}-clear-button`}
					/>
				)}
				{rightActionElement && rightActionElement}
			</div>
		);
	}
);
InputBase.displayName = "InputBase";

export { InputBase, type InputBaseProps, type InputSize };
